import { ReactNode, createContext, useContext } from 'react';
import * as Reach from '@reach/menu-button';
import styled from 'styled-components';
import { rgba } from 'polished';

import { c1D1D37, cFFFFFF } from '#/colors';

// Listbox API below was created to be as close as possible to Reach's Listbox
// component so that it can be easily replaced when it's updated to support React 18.
// Progress on that can be tracked here: https://github.com/reach/reach-ui/issues/916.
// Styles will still require changes on the selectors as they differ from Menu Button to Listbox.

const onChangeCtx = createContext<(value: string) => void>(() => {
  /* noop */
});

const labelCtx = createContext('');

interface ListboxInputProps
  extends Pick<React.HTMLAttributes<HTMLDivElement>, 'aria-labelledby'> {
  readonly children: ReactNode;
  readonly value: string;
  readonly label: string;
  readonly onChange: (value: string) => void;
}

export function ListboxInput(props: ListboxInputProps) {
  const { onChange, children, label, ...rest } = props;

  return (
    <onChangeCtx.Provider value={onChange}>
      <labelCtx.Provider value={label}>
        <Reach.Menu {...rest}>{children}</Reach.Menu>
      </labelCtx.Provider>
    </onChangeCtx.Provider>
  );
}

const MenuItems = styled(Reach.MenuItems)`
  padding: 8px 12px;
  background-color: ${c1D1D37};
  border: 1px solid ${c1D1D37};
  border-top: none;
  border-radius: 5px;
  position: absolute;
  left: 0px;
  right: 0px;
  top: 65px;
  font-size: 14px;
  z-index: 1;

  [data-reach-menu-item] {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 30px;
    padding: 0;
    cursor: pointer;
    color: ${rgba(cFFFFFF, 0.7)};
    line-height: 14px;

    &:hover {
      color: ${cFFFFFF};
    }

    &[aria-disabled='true'] {
      color: ${cFFFFFF};
      cursor: default;
    }
  }
`;

export const ListboxList = MenuItems;

export const ListboxPopover = Reach.MenuPopover;

interface ListboxOptionProps {
  readonly value: string;
  readonly disabled?: boolean;
  readonly children: ReactNode;
}

export function ListboxOption(props: ListboxOptionProps) {
  const onSelect = useContext(onChangeCtx);
  return (
    <Reach.MenuItem
      onSelect={() => onSelect(props.value)}
      disabled={props.disabled}
    >
      {props.children}
    </Reach.MenuItem>
  );
}

interface ListboxButtonProps {
  readonly arrow: ReactNode;
}

export function ListboxButton(props: ListboxButtonProps) {
  const label = useContext(labelCtx);
  return (
    <MenuButton>
      {label} {props.arrow}
    </MenuButton>
  );
}

const MenuButton = styled(Reach.MenuButton)`
  background: none;
  border: none;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  color: currentColor;
  font-size: 14px;
`;
